import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Chip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { apiFetch } from '../../api'
import socket from '../../socket'
import CrearVendedorForm from '../components/CrearVendedorForm'
import GestionAdminPanel from '../components/GestionAdminPanel'
import VehiculoFormDialog from '../components/VehiculoFormDialog'

const emptyVendedor = { nombre: '', telefono: '', email: '' }
const emptyVehiculo = { modelo: '' }

const normalize = (value) => String(value || '').trim().toLowerCase()

function ReassignOnDeactivateDialog({
  open,
  vendedor,
  consultas = [],
  vendedoresDestino = [],
  assignments = {},
  selectedConsultaId,
  loading,
  saving,
  error,
  onClose,
  onSelectConsulta,
  onAssign,
  onConfirm,
}) {
  const selectedConsulta = consultas.find((consulta) => String(consulta.id_ven_cli) === String(selectedConsultaId)) || consultas[0] || null
  const assignedCount = consultas.filter((consulta) => assignments[consulta.id_ven_cli]).length
  const allAssigned = consultas.length > 0 && assignedCount === consultas.length
  const noDestinations = !loading && vendedoresDestino.length === 0

  const estadoLabel = (estado) => {
    const value = String(estado || '').toUpperCase()
    if (value === 'ACTIVO') return 'Activo'
    if (value === 'FUTURO') return 'Futuro'
    return value || '-'
  }

  const vendedorName = (id) => vendedoresDestino.find((v) => String(v.id) === String(id))?.nombre || ''

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: '82vw', maxWidth: 940, height: '78vh' } }}
    >
      <DialogTitle sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
              Reasignar consultas
            </Typography>
            <Typography sx={{ color: '#4c5d86', fontSize: 13, mt: 0.25 }}>
              El vendedor que deseas desactivar tiene consultas activas/futuras.
            </Typography>
            {vendedor?.nombre && (
              <Typography sx={{ color: '#4c5d86', fontSize: 12, mt: 0.25, fontWeight: 700 }}>
                Vendedor: {vendedor.nombre}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {(loading || saving) && <LinearProgress />}

      <DialogContent sx={{ p: 0, minHeight: 0 }}>
        <Stack spacing={1.5} sx={{ px: 2, pt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && noDestinations && (
            <Alert severity="warning">
              No hay otros vendedores activos disponibles para reasignar estas consultas.
            </Alert>
          )}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
            gap: 2,
            p: 2,
            height: 'calc(100% - 16px)',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight="700">
                Consultas a reasignar ({consultas.length})
              </Typography>
              <Typography sx={{ color: '#4c5d86', fontSize: 12, fontWeight: 700 }}>
                {assignedCount}/{consultas.length}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
              <Stack spacing={1}>
                {consultas.map((consulta) => {
                  const isSelected = String(selectedConsulta?.id_ven_cli) === String(consulta.id_ven_cli)
                  const assignedName = vendedorName(assignments[consulta.id_ven_cli])
                  return (
                    <Box
                      key={consulta.id_ven_cli}
                      onClick={() => onSelectConsulta?.(consulta.id_ven_cli)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        bgcolor: isSelected ? 'rgba(25,118,210,0.08)' : '#fff',
                        cursor: saving ? 'default' : 'pointer',
                        opacity: saving ? 0.65 : 1,
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800 }} noWrap>
                            {consulta.cliente_nombre}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: '#4c5d86' }} noWrap>
                            {consulta.consulta || '-'}
                          </Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                          {assignedName && (
                            <CheckCircleIcon sx={{ color: '#12833d', fontSize: 22 }} />
                          )}
                          <Chip
                            size="small"
                            label={estadoLabel(consulta.estado)}
                            sx={{ fontWeight: 800, flexShrink: 0 }}
                          />
                        </Stack>
                      </Stack>
                      <Typography sx={{ mt: 1, fontSize: 12, color: assignedName ? '#12833d' : '#9aa3b2', fontWeight: 800 }}>
                        {assignedName ? `Destino: ${assignedName}` : 'Sin destino asignado'}
                      </Typography>
                    </Box>
                  )
                })}

                {!loading && consultas.length === 0 && (
                  <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Este vendedor no tiene consultas activas ni futuras.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" fontWeight="700">
                Vendedor destino
              </Typography>
              {selectedConsulta && (
                <Typography sx={{ mt: 0.25, color: '#4c5d86', fontSize: 12 }}>
                  Para {selectedConsulta.cliente_nombre}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
              <Stack spacing={1}>
                {vendedoresDestino.map((destino) => {
                  const isSelected = selectedConsulta && String(assignments[selectedConsulta.id_ven_cli]) === String(destino.id)
                  return (
                    <Box
                      key={destino.id}
                      onClick={() => selectedConsulta && onAssign?.(selectedConsulta.id_ven_cli, destino.id)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        bgcolor: isSelected ? 'rgba(25,118,210,0.08)' : '#fff',
                        cursor: (!selectedConsulta || saving) ? 'default' : 'pointer',
                        opacity: (!selectedConsulta || saving) ? 0.65 : 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 13, fontWeight: isSelected ? 900 : 700 }}>
                          {destino.nombre}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 12, color: '#4c5d86' }}>
                          {destino.email || destino.telefono || ''}
                        </Typography>
                      </Box>
                      <Chip label="ACTIVO" size="small" color="success" sx={{ fontWeight: 800, flexShrink: 0 }} />
                    </Box>
                  )
                })}
              </Stack>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving || loading || noDestinations || !allAssigned}
        >
          {saving ? 'Desactivando...' : `Reasignar y desactivar (${assignedCount}/${consultas.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function GestionVendedoresVehiculos() {
  const [vendedoresSearch, setVendedoresSearch] = useState('')
  const [vehiculosSearch, setVehiculosSearch] = useState('')
  const [ocultarInactivos, setOcultarInactivos] = useState({ vendedores: false, vehiculos: false })
  const [vendedores, setVendedores] = useState([])
  const [vehiculos, setVehiculos] = useState([])
  const [vendedoresLoading, setVendedoresLoading] = useState(false)
  const [vehiculosLoading, setVehiculosLoading] = useState(false)
  const [vendedoresError, setVendedoresError] = useState('')
  const [vehiculosError, setVehiculosError] = useState('')

  const [vendedorDialogOpen, setVendedorDialogOpen] = useState(false)
  const [vendedorForm, setVendedorForm] = useState(emptyVendedor)
  const [editingVendedorId, setEditingVendedorId] = useState(null)
  const [vendedorSaving, setVendedorSaving] = useState(false)
  const [vendedorError, setVendedorError] = useState('')

  const [vehiculoDialogOpen, setVehiculoDialogOpen] = useState(false)
  const [vehiculoForm, setVehiculoForm] = useState(emptyVehiculo)
  const [editingVehiculoId, setEditingVehiculoId] = useState(null)
  const [vehiculoSaving, setVehiculoSaving] = useState(false)
  const [vehiculoError, setVehiculoError] = useState('')

  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', item: null, impact: null, loadingImpact: false, impactError: '' })
  const [reassignDialog, setReassignDialog] = useState({
    open: false,
    vendedor: null,
    consultas: [],
    vendedoresDestino: [],
    assignments: {},
    selectedConsultaId: null,
    loading: false,
    saving: false,
    error: '',
  })

  const parseApiError = async (res, fallback) => {
    if (!res) return fallback
    let data = null
    try {
      const contentType = res.headers?.get?.('content-type') || ''
      data = contentType.includes('application/json') ? await res.json() : { detail: await res.text() }
    } catch {
      data = null
    }
    if (Array.isArray(data?.detail)) return 'Revisá los datos del formulario'
    return data?.detail || fallback
  }

  const fetchVendedores = useCallback(async () => {
    setVendedoresLoading(true)
    setVendedoresError('')
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '1000',
        ocultar_inactivos: String(ocultarInactivos.vendedores),
      })
      const res = await apiFetch(`/api/vendedores/listado?${params.toString()}`, { credentials: 'include' })
      if (!res.ok) throw new Error('No se pudo cargar vendedores')
      const data = await res.json()
      setVendedores(Array.isArray(data.items) ? data.items : [])
    } catch (e) {
      setVendedoresError(e.message || 'No se pudo cargar vendedores')
      setVendedores([])
    } finally {
      setVendedoresLoading(false)
    }
  }, [ocultarInactivos.vendedores])

  const fetchVehiculos = useCallback(async () => {
    setVehiculosLoading(true)
    setVehiculosError('')
    try {
      const params = new URLSearchParams({
        ocultar_inactivos: String(ocultarInactivos.vehiculos),
      })
      if (vehiculosSearch.trim()) params.set('q', vehiculosSearch.trim())
      const res = await apiFetch(`/api/vehiculos/?${params.toString()}`, { credentials: 'include' })
      if (!res.ok) throw new Error('No se pudo cargar vehículos')
      const data = await res.json()
      setVehiculos(Array.isArray(data) ? data : [])
    } catch (e) {
      setVehiculosError(e.message || 'No se pudo cargar vehículos')
      setVehiculos([])
    } finally {
      setVehiculosLoading(false)
    }
  }, [ocultarInactivos.vehiculos, vehiculosSearch])

  useEffect(() => {
    fetchVendedores()
  }, [fetchVendedores])

  useEffect(() => {
    fetchVehiculos()
  }, [fetchVehiculos])

  useEffect(() => {
    const refresh = () => {
      fetchVendedores()
    }
    socket.on('vendedor_toggled', refresh)
    socket.on('vendedor_created', refresh)
    socket.on('vendedor_updated', refresh)
    socket.on('vendedor_deleted', refresh)
    return () => {
      socket.off('vendedor_toggled', refresh)
      socket.off('vendedor_created', refresh)
      socket.off('vendedor_updated', refresh)
      socket.off('vendedor_deleted', refresh)
    }
  }, [fetchVendedores])

  const visibleVendedores = useMemo(() => {
    const term = normalize(vendedoresSearch)
    if (!term) return vendedores
    return vendedores.filter((vendedor) => (
      normalize(vendedor.nombre).includes(term)
      || normalize(vendedor.email).includes(term)
      || normalize(vendedor.telefono).includes(term)
    ))
  }, [vendedoresSearch, vendedores])

  const handleCreate = (type) => {
    if (type === 'vendedores') {
      setEditingVendedorId(null)
      setVendedorForm(emptyVendedor)
      setVendedorError('')
      setVendedorDialogOpen(true)
      return
    }
    setEditingVehiculoId(null)
    setVehiculoForm(emptyVehiculo)
    setVehiculoError('')
    setVehiculoDialogOpen(true)
  }

  const handleEdit = (type, item) => {
    if (type === 'vendedores') {
      setEditingVendedorId(item.id)
      setVendedorForm({
        nombre: item.nombre || '',
        telefono: item.telefono || '',
        email: item.email || '',
      })
      setVendedorError('')
      setVendedorDialogOpen(true)
      return
    }
    setEditingVehiculoId(item.id)
    setVehiculoForm({ modelo: item.modelo || '' })
    setVehiculoError('')
    setVehiculoDialogOpen(true)
  }

  const handleVendedorChange = (event) => {
    const { name, value } = event.target
    if (name === 'telefono') {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 10)
      setVendedorForm((prev) => ({ ...prev, telefono: digits }))
      return
    }
    setVendedorForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleVehiculoChange = (event) => {
    const { name, value } = event.target
    setVehiculoForm((prev) => ({ ...prev, [name]: value }))
  }

  const saveVendedor = async (event) => {
    event?.preventDefault?.()
    setVendedorSaving(true)
    setVendedorError('')
    const payload = {
      nombre: vendedorForm.nombre,
      telefono: vendedorForm.telefono.trim() === '' ? null : vendedorForm.telefono,
      email: vendedorForm.email.trim() === '' ? null : vendedorForm.email.trim(),
    }
    try {
      const isEditing = Boolean(editingVendedorId)
      const res = await apiFetch(isEditing ? `/api/vendedores/${editingVendedorId}` : '/api/vendedores/', {
        method: isEditing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(await parseApiError(res, 'No se pudo guardar el vendedor'))
      setVendedorDialogOpen(false)
      setEditingVendedorId(null)
      setVendedorForm(emptyVendedor)
      await fetchVendedores()
    } catch (e) {
      setVendedorError(e.message || 'No se pudo guardar el vendedor')
    } finally {
      setVendedorSaving(false)
    }
  }

  const saveVehiculo = async (event) => {
    event?.preventDefault?.()
    setVehiculoSaving(true)
    setVehiculoError('')
    const payload = { modelo: vehiculoForm.modelo.trim() }
    try {
      const isEditing = Boolean(editingVehiculoId)
      const res = await apiFetch(isEditing ? `/api/vehiculos/${editingVehiculoId}` : '/api/vehiculos/', {
        method: isEditing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(await parseApiError(res, 'No se pudo guardar el vehículo'))
      setVehiculoDialogOpen(false)
      setEditingVehiculoId(null)
      setVehiculoForm(emptyVehiculo)
      await fetchVehiculos()
    } catch (e) {
      setVehiculoError(e.message || 'No se pudo guardar el vehículo')
    } finally {
      setVehiculoSaving(false)
    }
  }

  const closeConfirm = () => setConfirmDialog({ open: false, type: '', item: null, impact: null, loadingImpact: false, impactError: '' })

  const closeReassignDialog = () => {
    if (reassignDialog.saving) return
    setReassignDialog({
      open: false,
      vendedor: null,
      consultas: [],
      vendedoresDestino: [],
      assignments: {},
      selectedConsultaId: null,
      loading: false,
      saving: false,
      error: '',
    })
  }

  const openReassignDialog = async (item) => {
    setReassignDialog({
      open: true,
      vendedor: item,
      consultas: [],
      vendedoresDestino: [],
      assignments: {},
      selectedConsultaId: null,
      loading: true,
      saving: false,
      error: '',
    })
    try {
      const res = await apiFetch(`/api/vendedores/${item.id}/reasignacion-pendiente`, { credentials: 'include' })
      if (!res.ok) throw new Error(await parseApiError(res, 'No se pudo cargar la reasignación pendiente'))
      const data = await res.json()
      const consultas = Array.isArray(data.consultas) ? data.consultas : []
      const vendedoresDestino = Array.isArray(data.vendedores_destino) ? data.vendedores_destino : []
      setReassignDialog((prev) => ({
        ...prev,
        vendedor: data.vendedor || item,
        consultas,
        vendedoresDestino,
        selectedConsultaId: consultas[0]?.id_ven_cli ?? null,
        loading: false,
        error: '',
      }))
    } catch (e) {
      setReassignDialog((prev) => ({
        ...prev,
        loading: false,
        error: e.message || 'No se pudo cargar la reasignación pendiente',
      }))
    }
  }

  const handleSelectReassignConsulta = (idVenCli) => {
    if (reassignDialog.saving) return
    setReassignDialog((prev) => ({ ...prev, selectedConsultaId: idVenCli }))
  }

  const handleAssignReassignConsulta = (idVenCli, vendedorDestinoId) => {
    if (reassignDialog.saving) return
    setReassignDialog((prev) => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        [idVenCli]: vendedorDestinoId,
      },
    }))
  }

  const confirmDeactivateWithReassign = async () => {
    const vendedor = reassignDialog.vendedor
    if (!vendedor?.id) return
    const reasignaciones = reassignDialog.consultas.map((consulta) => ({
      id_ven_cli: Number(consulta.id_ven_cli),
      id_nuevo_vendedor: Number(reassignDialog.assignments[consulta.id_ven_cli]),
    }))
    if (reasignaciones.some((item) => !item.id_nuevo_vendedor)) {
      setReassignDialog((prev) => ({ ...prev, error: 'Asigná un vendedor destino a cada consulta.' }))
      return
    }

    setReassignDialog((prev) => ({ ...prev, saving: true, error: '' }))
    try {
      const res = await apiFetch(`/api/vendedores/${vendedor.id}/desactivar-con-reasignacion`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reasignaciones }),
      })
      if (!res.ok) throw new Error(await parseApiError(res, 'No se pudo desactivar el vendedor'))
      closeReassignDialog()
      await fetchVendedores()
    } catch (e) {
      setReassignDialog((prev) => ({
        ...prev,
        saving: false,
        error: e.message || 'No se pudo desactivar el vendedor',
      }))
    }
  }

  const handleToggleStatus = async (type, item) => {
    if (type === 'vehiculos') {
      setConfirmDialog({ open: true, type: 'vehiculo', item, impact: null, loadingImpact: false, impactError: '' })
      return
    }

    setConfirmDialog({ open: true, type: 'vendedor', item, impact: null, loadingImpact: Boolean(item?.activo), impactError: '' })
    if (!item?.activo) return

    try {
      const res = await apiFetch(`/api/vendedores/${item.id}/estados_count`, { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const hasActiveOrFuture = (data.ACTIVO || 0) > 0 || (data.FUTURO || 0) > 0
      if (hasActiveOrFuture) {
        closeConfirm()
        await openReassignDialog(item)
        return
      }
      setConfirmDialog({
        open: true,
        type: 'vendedor',
        item,
        loadingImpact: false,
        impact: null,
        impactError: '',
      })
    } catch {
      setConfirmDialog({
        open: true,
        type: 'vendedor',
        item,
        impact: null,
        loadingImpact: false,
        impactError: 'No se pudo calcular el impacto antes de la baja.',
      })
    }
  }

  const confirmToggleStatus = async () => {
    const { type, item } = confirmDialog
    if (!item) return
    closeConfirm()
    try {
      if (type === 'vehiculo') {
        const res = await apiFetch(`/api/vehiculos/${item.id}/alta_baja`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ activo: !item.activo }),
        })
        if (!res.ok) throw new Error(await parseApiError(res, 'No se pudo cambiar el estado del vehículo'))
        await fetchVehiculos()
        return
      }

      const res = await apiFetch(`/api/vendedores/${item.id}/alta_baja`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ activo: !item.activo }),
      })
      if (!res.ok) throw new Error(await parseApiError(res, 'No se pudo cambiar el estado del vendedor'))
      await fetchVendedores()
    } catch (e) {
      if (type === 'vehiculo') {
        setVehiculosError(e.message || 'No se pudo cambiar el estado')
      } else {
        setVendedoresError(e.message || 'No se pudo cambiar el estado')
      }
    }
  }

  const blockingAssignments = Boolean(
    confirmDialog.type === 'vendedor'
    && confirmDialog.item?.activo
    && confirmDialog.impact
    && ((confirmDialog.impact.ACTIVO || 0) > 0 || (confirmDialog.impact.FUTURO || 0) > 0)
  )

  const confirmTitle = confirmDialog.type === 'vehiculo'
    ? `Vehículo: ${confirmDialog.item?.modelo || ''}`
    : `Vendedor: ${confirmDialog.item?.nombre || ''}`

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 3, md: 5 },
          flexWrap: 'wrap',
        }}
      >
        <GestionAdminPanel
          tab="vendedores"
          search={vendedoresSearch}
          onSearchChange={setVendedoresSearch}
          ocultarInactivos={ocultarInactivos.vendedores}
          onOcultarInactivosChange={(value) => setOcultarInactivos((prev) => ({ ...prev, vendedores: value }))}
          vendedores={visibleVendedores}
          vehiculos={[]}
          loading={vendedoresLoading}
          error={vendedoresError}
          onCreate={() => handleCreate('vendedores')}
          onEdit={(item) => handleEdit('vendedores', item)}
          onToggleStatus={(item) => handleToggleStatus('vendedores', item)}
        />

        <GestionAdminPanel
          tab="vehiculos"
          search={vehiculosSearch}
          onSearchChange={setVehiculosSearch}
          ocultarInactivos={ocultarInactivos.vehiculos}
          onOcultarInactivosChange={(value) => setOcultarInactivos((prev) => ({ ...prev, vehiculos: value }))}
          vendedores={[]}
          vehiculos={vehiculos}
          loading={vehiculosLoading}
          error={vehiculosError}
          onCreate={() => handleCreate('vehiculos')}
          onEdit={(item) => handleEdit('vehiculos', item)}
          onToggleStatus={(item) => handleToggleStatus('vehiculos', item)}
        />
      </Box>

      <CrearVendedorForm
        open={vendedorDialogOpen}
        onClose={() => setVendedorDialogOpen(false)}
        title={editingVendedorId ? `Vendedor: ${vendedorForm.nombre || 'Editar'}` : 'Nuevo vendedor'}
        submitLabel={editingVendedorId ? 'Guardar cambios' : 'Crear vendedor'}
        form={vendedorForm}
        onChange={handleVendedorChange}
        onSubmit={saveVendedor}
        error={vendedorError}
        loading={vendedorSaving}
      />

      <VehiculoFormDialog
        open={vehiculoDialogOpen}
        onClose={() => setVehiculoDialogOpen(false)}
        title={editingVehiculoId ? `Vehículo: ${vehiculoForm.modelo || 'Editar'}` : 'Nuevo vehículo'}
        submitLabel={editingVehiculoId ? 'Guardar cambios' : 'Crear vehículo'}
        form={vehiculoForm}
        onChange={handleVehiculoChange}
        onSubmit={saveVehiculo}
        error={vehiculoError}
        loading={vehiculoSaving}
      />

      <ReassignOnDeactivateDialog
        open={reassignDialog.open}
        vendedor={reassignDialog.vendedor}
        consultas={reassignDialog.consultas}
        vendedoresDestino={reassignDialog.vendedoresDestino}
        assignments={reassignDialog.assignments}
        selectedConsultaId={reassignDialog.selectedConsultaId}
        loading={reassignDialog.loading}
        saving={reassignDialog.saving}
        error={reassignDialog.error}
        onClose={closeReassignDialog}
        onSelectConsulta={handleSelectReassignConsulta}
        onAssign={handleAssignReassignConsulta}
        onConfirm={confirmDeactivateWithReassign}
      />

      <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 800 }}>{confirmTitle}</Typography>
          <IconButton onClick={closeConfirm} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#333' }}>
            ¿Seguro que querés {confirmDialog.item?.activo ? 'desactivar' : 'activar'} este {confirmDialog.type === 'vehiculo' ? 'vehículo' : 'vendedor'}?
          </Typography>
          {confirmDialog.loadingImpact && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Calculando impacto comercial...</Typography>
            </Box>
          )}
          {confirmDialog.impactError && <Alert severity="warning" sx={{ mt: 2 }}>{confirmDialog.impactError}</Alert>}
          {confirmDialog.type === 'vendedor' && confirmDialog.item?.activo && confirmDialog.impact && (
            <Alert severity={blockingAssignments ? 'warning' : 'info'} sx={{ mt: 2 }}>
              Tiene {confirmDialog.impact.ACTIVO} leads activos, {confirmDialog.impact.FUTURO} clientes en futuro y {confirmDialog.impact.VENTA} ventas registradas. Solo las consultas en ACTIVO o FUTURO requieren reasignación antes de desactivar.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirm} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={confirmToggleStatus} disabled={confirmDialog.loadingImpact || blockingAssignments}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
